import { Link, useRouter } from '@tanstack/react-router';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { CircleAlert } from 'lucide-react';
import { isApiClientError } from '../../lib/api';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { Icon } from '../ui/Icon';
import { Page } from '../ui/Page';

const HTTP_FORBIDDEN = 403;

export interface RouteErrorMessageProps {
  readonly error: unknown;
  readonly onRetry?: () => void;
}

function describe(error: unknown): { title: string; detail: string; code: string | null } {
  if (isApiClientError(error)) {
    if (error.status === HTTP_FORBIDDEN) return { title: 'Нет доступа', detail: error.message, code: null };
    const title = error.status === 0 ? 'Локальный сервер недоступен' : 'Сервер вернул ошибку';
    return { title, detail: error.message, code: `${error.code}${error.status ? ` · ${error.status}` : ''}` };
  }
  const title = 'Что-то пошло не так';
  if (error instanceof Error) return { title, detail: error.message, code: null };
  return { title, detail: String(error), code: null };
}

/** Inline API error box: danger tone, message, code and an optional retry. */
export function RouteErrorMessage({ error, onRetry }: RouteErrorMessageProps) {
  const { title, detail, code } = describe(error);
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-2 border border-border-1 bg-danger-bg p-3 text-danger-fg"
    >
      <Icon icon={CircleAlert} className="mt-px" />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="font-semibold">{title}</span>
        <span className="text-fg-1">{detail}</span>
        {code && <span className="text-[12px] text-fg-2">{code}</span>}
      </div>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Повторить
        </Button>
      )}
    </div>
  );
}

/** Route `errorComponent`: the error renders inside the shell instead of a blank screen. */
export function RouteErrorBoundary({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <Page>
      <RouteErrorMessage
        error={error}
        onRetry={() => {
          reset();
          void router.invalidate();
        }}
      />
    </Page>
  );
}

/** Route `pendingComponent`. */
export function RoutePending() {
  return (
    <Page>
      <div role="status" aria-live="polite" className="text-fg-3">
        Загрузка…
      </div>
    </Page>
  );
}

/** Router `defaultNotFoundComponent`. */
export function RouteNotFound() {
  return (
    <Page>
      <EmptyState
        title="Страница не найдена"
        description="По этому адресу нет ни одного экрана."
        actions={
          <Link to="/" className="no-underline">
            Перейти к обзору
          </Link>
        }
      />
    </Page>
  );
}
